<template>
	<div class="chat-layer-settings">
		<h6>Behavior</h6>
		<b-form-group label="Alignment" label-size="sm" class="mb-2">
			<b-button-group size="sm" class="d-flex">
				<b-button :pressed="settings.alignment !== 'bottom'" variant="outline-secondary" @click="set('alignment', 'top')">Top</b-button>
				<b-button :pressed="settings.alignment === 'bottom'" variant="outline-secondary" @click="set('alignment', 'bottom')">Bottom</b-button>
			</b-button-group>
		</b-form-group>
		<slider-form-group label="Scroll speed" :min="0" :max="100" :step="1" :value="scrollSpeedPosition" :reset-value="defaultScrollSpeedPosition" class="mb-2" @input="onScrollSpeed">
			<span class="text-muted small">{{scrollSpeedLabel}}</span>
		</slider-form-group>

		<h6>Options</h6>
		<b-form-checkbox switch v-model="settings.enableTTS" @change="$emit('save')">Enable text-to-speech</b-form-checkbox>
		<b-form-checkbox switch v-model="settings.enableEventSounds" @change="$emit('save')">Enable event sounds</b-form-checkbox>
		<volume-slider class="mt-2" :value="Number(settings.volume)" @input="set('volume', $event)" />

		<h6>Animation</h6>
		<b-form-group label="In transition" label-size="sm" class="mb-2">
			<b-form-select size="sm" v-model="settings.inTransition" :options="inTransitions" @change="$emit('save')" />
		</b-form-group>
		<b-form-group label="Out transition" label-size="sm" class="mb-2">
			<b-form-select size="sm" v-model="settings.outTransition" :options="outTransitions" @change="$emit('save')" />
		</b-form-group>
		<b-form-row class="mb-2">
			<b-col><label class="small mb-0">Duration</label><b-input-group size="sm" append="ms"><b-form-input type="number" min="0" max="5000" v-model.number="settings.duration" @change="$emit('save')" /></b-input-group></b-col>
			<b-col><label class="small mb-0">Linger</label><b-input-group size="sm" append="ms"><b-form-input type="number" min="0" max="60000" :disabled="!settings.outTransition" v-model.number="settings.linger" @change="$emit('save')" /></b-input-group></b-col>
		</b-form-row>
		<b-form-group label="Load timeout" label-size="sm" class="mb-2">
			<b-input-group size="sm" append="ms"><b-form-input type="number" min="0" max="100000" v-model.number="settings.loadTimeout" @change="$emit('save')" /></b-input-group>
		</b-form-group>

		<h6>Text</h6>
		<b-form-group label="Font family" label-size="sm" class="mb-2">
			<google-font-input :value="settings.fontFamily || ''" @input="set('fontFamily', $event)" />
		</b-form-group>
		<b-form-row class="mb-2">
			<b-col><label class="small mb-0">Font size</label><b-input-group size="sm" append="px"><b-form-input type="number" min="0" max="999" step="any" v-model.number="settings.fontSize" @change="$emit('save')" /></b-input-group></b-col>
			<b-col><label class="small mb-0">Spacing</label><b-input-group size="sm" append="px"><b-form-input type="number" min="0" max="999" step="any" v-model.number="settings.letterSpacing" @change="$emit('save')" /></b-input-group></b-col>
		</b-form-row>
		<b-form-group label="Align" label-size="sm" class="mb-2">
			<b-button-group size="sm" class="d-flex">
				<b-button v-for="a in textAligns" :key="a.value" v-b-tooltip.hover.bottom :title="a.title" :pressed="settings.textAlign === a.value" variant="outline-secondary" @click="set('textAlign', a.value)"><b-icon :icon="a.icon" /></b-button>
			</b-button-group>
		</b-form-group>
		<b-form-group label="Formatting" label-size="sm" class="mb-2">
			<b-button-group size="sm" class="d-flex">
				<b-button v-for="f in formats" :key="f.key" v-b-tooltip.hover.bottom :title="f.title" :pressed="!!settings[f.key]" variant="outline-secondary" @click="set(f.key, !settings[f.key])"><b-icon :icon="f.icon" /></b-button>
			</b-button-group>
		</b-form-group>

		<h6>Colors</h6>
		<b-form-group label="Text color" label-size="sm" class="mb-2"><color-picker :value="settings.textColor" @input="set('textColor', $event)" /></b-form-group>
		<b-form-group label="Message background" label-size="sm" class="mb-2"><color-picker :value="settings.textBg" @input="set('textBg', $event)" /></b-form-group>
		<b-form-group label="Gift text color" label-size="sm" class="mb-2"><color-picker :value="settings.giftColor" @input="set('giftColor', $event)" /></b-form-group>
		<b-form-group label="Gift background" label-size="sm" class="mb-2"><color-picker :value="settings.giftBg" @input="set('giftBg', $event)" /></b-form-group>

		<h6>Outline</h6>
		<b-form-row class="mb-2">
			<b-col><label class="small mb-0">Blur</label><b-input-group size="sm" append="px"><b-form-input type="number" min="0" max="100" step="any" v-model.number="settings.blurRadius" @change="$emit('save')" /></b-input-group></b-col>
			<b-col><label class="small mb-0">Size</label><b-input-group size="sm" append="px"><b-form-input type="number" min="0" max="10" step="any" v-model.number="settings.spreadRadius" @change="$emit('save')" /></b-input-group></b-col>
		</b-form-row>
		<b-form-group label="Outline color" label-size="sm" class="mb-0"><color-picker :value="settings.shadowColor" @input="set('shadowColor', $event)" /></b-form-group>
	</div>
</template>

<script>
import animateCssTransitions from '@/animateCssTransitions';
import ColorPicker from '@/components/ColorPicker.vue';
import GoogleFontInput from '@/components/GoogleFontInput.vue';
import SliderFormGroup from '@/components/SliderFormGroup.vue';
import VolumeSlider from '@/components/VolumeSlider.vue';
import { getDefaultChatSettings, calcScrollSpeed, calcSliderPosition, DEFAULT_SCROLL_SPEED } from '@/overlay/chatSettings';

export default {
	components: { ColorPicker, GoogleFontInput, SliderFormGroup, VolumeSlider },
	props: {
		settings: { type: Object, required: true },
	},
	data() { return {
		inTransitions: [ { text: 'None', value: null }, ...animateCssTransitions.inTransitions ],
		outTransitions: [ { text: 'None (stay until pushed out)', value: null }, ...animateCssTransitions.outTransitions ],
		textAligns: [
			{ value: 'left', icon: 'text-left', title: 'Align left' },
			{ value: 'center', icon: 'text-center', title: 'Align center' },
			{ value: 'right', icon: 'text-right', title: 'Align right' },
		],
		formats: [
			{ key: 'boldText', icon: 'type-bold', title: 'Bold' },
			{ key: 'italicText', icon: 'type-italic', title: 'Italic' },
			{ key: 'underlineText', icon: 'type-underline', title: 'Underline' },
			{ key: 'strikethroughText', icon: 'type-strikethrough', title: 'Strikethrough' },
			{ key: 'uppercaseText', icon: 'align-top', title: 'Uppercase' },
		],
		defaultScrollSpeedPosition: calcSliderPosition(DEFAULT_SCROLL_SPEED),
	}},
	computed: {
		scrollSpeedPosition() { return calcSliderPosition(Number(this.settings.scrollSpeed)); },
		scrollSpeedLabel() {
			if(this.scrollSpeedPosition < 34) return 'Slow';
			if(this.scrollSpeedPosition < 67) return 'Medium';
			return 'Fast';
		},
	},
	watch: {
		// Layers saved before a field existed lack its key, which Vue can't react to being added later.
		settings: {
			immediate: true,
			handler(settings) {
				for(const [ key, value ] of Object.entries(getDefaultChatSettings())) if(!(key in settings)) this.$set(settings, key, value);
			},
		},
	},
	methods: {
		set(key, value) {
			this.$set(this.settings, key, value);
			this.$emit('save');
		},
		onScrollSpeed(position) {
			this.set('scrollSpeed', calcScrollSpeed(position));
		},
	},
};
</script>

<style scoped>
h6 {
	margin: 1rem 0 .5rem;
	padding-bottom: .25rem;
	border-bottom: 1px solid rgba(255, 255, 255, .12);
	font-size: .8rem;
	text-transform: uppercase;
	letter-spacing: .04em;
	opacity: .75;
}

h6:first-child {
	margin-top: 0;
}
</style>
